import { type ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { fixingOrderApi } from '@/store/orders/fixing/api/index.ts';
import { isFixingOrder } from '@/store/orders/fixing/selectors.ts';
import type { FixingOrder, FixingOrderProperties, ValidateFixingOrderRequest } from '@/store/orders/fixing/state.ts';
import {
  isError400WithPayload,
  isSubmitOrderResponse,
  type OrderById,
  type ValidateOrderResponse,
} from '@/store/orders/state.ts';

const mapProperties = (
  currentOrder: FixingOrder,
  originalArgs: ValidateFixingOrderRequest,
  payload?: ValidateOrderResponse,
): FixingOrderProperties => {
  const currentTime = Date.now();
  const clockOffset =
    payload?.backendTime !== null && payload?.backendTime !== 0 && typeof payload?.backendTime === 'number'
      ? payload?.backendTime - currentTime
      : undefined;

  const oldFixingTime = originalArgs.fixingTime ?? currentOrder.properties.fixingTime;
  const newFixingTime = oldFixingTime ?? payload?.fixingTimesOfSelectedType?.[0];

  const oldFixingPlace = originalArgs.fixingPlace ?? currentOrder.properties.fixingPlace;
  const newFixingPlace = oldFixingPlace ?? payload?.sourceNameOfSelectedType?.[0];

  return {
    status: currentOrder.properties.status,
    isReadyToSubmit: Boolean(payload?.isReadyToSubmit),
    mode: currentOrder.properties.mode,
    clockOffset,

    currencyPair: {
      currency1: originalArgs.currency1,
      currency2: originalArgs.currency2,
      tiering: currentOrder.properties.currencyPair?.tiering,
    },

    clientWay: originalArgs.way ?? currentOrder.properties.clientWay,

    notional: originalArgs.notional ?? currentOrder.properties.notional,
    notionalCurrencyIndex: originalArgs.notionalCurrencyIndex ?? currentOrder.properties.notionalCurrencyIndex,

    fixingReference: originalArgs.fixingReference ?? currentOrder.properties.fixingReference,
    fixingPriceType: originalArgs.fixingPriceType ?? currentOrder.properties.fixingPriceType,
    fixingDateUtc: originalArgs.fixingDateUtc ?? currentOrder.properties.fixingDateUtc,
    expiryDay: originalArgs.expiryDay ?? currentOrder.properties.expiryDay,
    fixingTime: newFixingTime,
    fixingPlace: newFixingPlace,
    commercialMargin: payload?.marginInBps ?? originalArgs.commercialMargin ?? currentOrder.properties.commercialMargin,
    fixingMarginType: originalArgs.fixingMarginType ?? currentOrder.properties.fixingMarginType,

    fixingTimesOfSelectedType: isDefined(payload?.fixingTimesOfSelectedType)
      ? payload.fixingTimesOfSelectedType
      : currentOrder.properties.fixingTimesOfSelectedType,

    sourceNameOfSelectedType: isDefined(payload?.sourceNameOfSelectedType)
      ? payload.sourceNameOfSelectedType
      : currentOrder.properties.sourceNameOfSelectedType,

    fixingTypes: isDefined(payload?.fixingTypes) ? payload.fixingTypes : currentOrder.properties.fixingTypes,

    canModifyMarginInBps: payload?.canModifyMarginInBps ?? currentOrder.properties.canModifyMarginInBps,
    isStandardGroup: payload?.isStandardGroup ?? currentOrder.properties.isStandardGroup,
    isCcyForcedBidAsk: payload?.isCcyForcedBidAsk ?? currentOrder.properties.isCcyForcedBidAsk,

    notificationEmails: originalArgs.notificationEmails?.isOverridenEmails
      ? originalArgs.notificationEmails.emails
      : currentOrder.properties.notificationEmails,
  };
};

export const validateFixingFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(fixingOrderApi.endpoints.validateFixing.matchFulfilled, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];

    if (isNotDefined(currentOrder) || !isFixingOrder(currentOrder)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(action.meta.arg.originalArgs.currency1) || isNotDefined(action.meta.arg.originalArgs.currency2)) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,

      type: 'FixingOrder',
      clientId: currentOrder.clientId,

      properties: {
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, action.payload),
        errors: {},
      },
    };
  });

export const validateFixingPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(isAnyOf(fixingOrderApi.endpoints.validateFixing.matchPending), (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;
    const currentOrder = state[tileId];
    if (isNotDefined(currentOrder) || !isFixingOrder(currentOrder)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,
      properties: {
        ...currentOrder.properties,

        clientWay: action.meta.arg.originalArgs.way ?? currentOrder.properties.clientWay,
        notional: action.meta.arg.originalArgs.notional,
        notionalCurrencyIndex: action.meta.arg.originalArgs.notionalCurrencyIndex,

        fixingReference: action.meta.arg.originalArgs.fixingReference ?? currentOrder.properties.fixingReference,
        fixingPriceType: action.meta.arg.originalArgs.fixingPriceType ?? currentOrder.properties.fixingPriceType,
        expiryDay: action.meta.arg.originalArgs.expiryDay ?? currentOrder.properties.expiryDay,
        fixingTime: action.meta.arg.originalArgs.fixingTime ?? currentOrder.properties.fixingTime,
        fixingPlace: action.meta.arg.originalArgs.fixingPlace ?? currentOrder.properties.fixingPlace,
        commercialMargin: action.meta.arg.originalArgs.commercialMargin ?? currentOrder.properties.commercialMargin,
        fixingMarginType: action.meta.arg.originalArgs.fixingMarginType ?? currentOrder.properties.fixingMarginType,

        notificationEmails:
          action.meta.arg.originalArgs.notificationEmails?.emails ?? currentOrder.properties.notificationEmails,
      },
    };
  });

export const submitFixingRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(fixingOrderApi.endpoints.submitFixing.matchRejected, fixingOrderApi.endpoints.updateFixing.matchRejected),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentOrder = state[tileId];
      const payload = isSubmitOrderResponse(action.payload) ? action.payload : undefined;

      if (
        isNotDefined(currentOrder) ||
        isNotDefined(payload) ||
        currentOrder.type !== 'FixingOrder' ||
        payload.type !== 'failure'
      ) {
        return state;
      }

      const errorMessage = (() => {
        if (payload.errors.some((error) => error.errorDescription === 'timeout')) {
          return 'order.timeout';
        }
        if (payload.errors.some((error) => error.errorDescription === 'server.error')) {
          return 'order.serverError';
        }
        if (payload.errors.some((error) => error.errorDescription === 'order.unexpected')) {
          return 'order.unexpected';
        }
        return 'order.validationFailed';
      })();

      const areErrorsKnown = payload.errors.some((error) => error.field !== 'unknown');

      const errors = payload.errors.reduce(
        (acc, error) => {
          if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
            acc.notional = error;
          }

          if (error.field === 'base64Emails') {
            acc.notificationEmails = error;
          }

          if (error.field === 'fixingBenchmark') {
            acc.fixingReference = error;
          }

          if (error.field === 'marginInBps') {
            acc.commercialMargin = error;
          }

          acc[error.field] = error;
          return acc;
        },
        {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
      );

      const actionType = action.meta.arg.endpointName;

      state[tileId] = {
        ...currentOrder,
        type: 'FixingOrder',
        properties: {
          ...currentOrder.properties,
          status: areErrorsKnown
            ? { type: actionType === 'submitFixing' ? 'creating' : 'submitted' }
            : { type: 'failed', errorMessage },
          errors: areErrorsKnown ? errors : {},
        },
      } as FixingOrder;
    },
  );

export const validateFixingRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(fixingOrderApi.endpoints.validateFixing.matchRejected, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];
    const payload = isError400WithPayload(action.payload) ? action.payload : undefined;

    if (isNotDefined(currentOrder) || currentOrder.type !== 'FixingOrder') {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(payload)) {
      state[tileId] = {
        ...currentOrder,
        type: 'FixingOrder',
        properties: {
          ...currentOrder.properties,
          status: { type: 'failed', errorMessage: 'order.unexpected' },
        },
      } as FixingOrder;
      return;
    }

    const errors = isDefined(payload?.validationResponseDetailContent)
      ? payload.validationResponseDetailContent.reduce(
          (acc, error) => {
            if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
              acc.notional = error;
            }

            if (error.field === 'base64Emails') {
              acc.notificationEmails = error;
            }

            if (error.field === 'fixingBenchmark') {
              acc.fixingReference = error;
            }

            if (error.field === 'marginInBps') {
              acc.commercialMargin = error;
            }

            acc[error.field] = error;
            return acc;
          },
          {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
        )
      : undefined;

    state[tileId] = {
      ...currentOrder,
      type: 'FixingOrder',
      properties: {
        ...currentOrder.properties,
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, payload),
        errors,
      },
    } as FixingOrder;
  });

export const submitFixingFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(fixingOrderApi.endpoints.updateFixing.matchFulfilled, fixingOrderApi.endpoints.submitFixing.matchFulfilled),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      if (isNotDefined(currentOrder) || !isFixingOrder(currentOrder) || action.payload.type === 'failure') {
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'submitted',
            tradeId: action.payload.orderId,
          },
          isReadyToSubmit: false,
        },
      };
    },
  );

export const submitFixingPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(fixingOrderApi.endpoints.updateFixing.matchPending, fixingOrderApi.endpoints.submitFixing.matchPending),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      if (isNotDefined(currentOrder) || !isFixingOrder(currentOrder)) {
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'pending',
          },
        },
      };
    },
  );
